"use client";

import { DelegationBanner } from "@/components/delegation/delegation-banner";
import { AppSidebar } from "@/components/layout/app-sidebar";
import { UnifiedHeader } from "@/components/layout/unified-header";
import { SidebarProvider } from "@/components/ui/sidebar";
import { HeaderPortalProvider } from "@/contexts/header-context";
import { NavigationProvider } from "@/contexts/navigation-context";
import { cn } from "@/lib/shared/utils";
import { useAuthStore } from "@/stores/auth-store";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect } from "react";

interface LayoutWrapperProps {
  children: React.ReactNode;
}

function LayoutWrapperContent({ children }: LayoutWrapperProps) {
  const { isAuthenticated, isInitialized } = useAuthStore();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  // Pages that should not show the sidebar even when authenticated
  const publicPages = ["/auth", "/register", "/setup", "/login"];
  const isPublicPage = publicPages.some(page => pathname === page || pathname.startsWith(`${page}/`));

  // Standalone pages that should render without sidebar/header
  const standalonePages = [
    "/widget-designer-preview",
    "/mobile",
    // Inline widget preview (#6) — chrome-less route for the Playwright
    // screenshot loop. Bypasses sidebar/header AND the auth gate so the
    // dev-only MCP can drive it without a logged-in session.
    "/widget-preview/inline",
  ];
  const isStandaloneParam = searchParams.get('standalone') === 'true';
  const isStandalonePage = standalonePages.some(page => pathname.startsWith(page)) || isStandaloneParam;

  // Redirect to login if not authenticated and not on a public page
  useEffect(() => {
    if (isInitialized && !isAuthenticated && !isPublicPage) {
      console.log("[Layout] Redirecting to login - not authenticated");
      // Include redirect param so user can return after login
      const currentUrl = pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');
      router.push(`/auth/login?redirect=${encodeURIComponent(currentUrl)}`);
    }
  }, [isAuthenticated, isInitialized, isPublicPage, pathname, searchParams, router]);

  // Show loading or nothing while auth is being initialized
  if (!isInitialized) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // If standalone page, render without sidebar/header (even when authenticated)
  if (isStandalonePage) {
    return (
      <div className="min-h-screen bg-background">
        {children}
      </div>
    );
  }

  // If not authenticated or on a public page, render without sidebar
  if (!isAuthenticated || isPublicPage) {
    // Show loading during redirect for protected pages
    if (!isAuthenticated && !isPublicPage) {
      return (
        <div className="flex h-screen items-center justify-center">
          <div className="text-muted-foreground">Redirecting to login...</div>
        </div>
      );
    }
    return <>{children}</>;
  }

  // If authenticated and not on a public page, render with sidebar
  return (
    <NavigationProvider>
      <HeaderPortalProvider>
        <SidebarProvider
          style={
            {
              "--sidebar-width": "calc(var(--spacing) * 72)",
              "--header-height": "calc(var(--spacing) * 12)",
            } as React.CSSProperties
          }
        >
          <AppSidebar variant="inset" />
          <div
            className={cn("bg-background relative flex w-full flex-1 flex-col")}
          >
            <UnifiedHeader />
            <DelegationBanner />
            <main className={cn("flex flex-1 flex-col p-0")}>{children}</main>
          </div>
        </SidebarProvider>
      </HeaderPortalProvider>
    </NavigationProvider>
  );
}

export function LayoutWrapper({ children }: LayoutWrapperProps) {
  return (
    <Suspense fallback={<div className="flex h-screen w-full items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
      <LayoutWrapperContent>{children}</LayoutWrapperContent>
    </Suspense>
  );
}
